import { Text, Flex } from '@chakra-ui/react';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { persistUserProperties } from '@app/actions/profile';
import { FEATURE_FLAG_INBOX_FILTERS } from '@app/constants/permissions';
import { useCheckPermissions } from '@app/helpers/common';
import { StateType } from '@app/types/reducer-state';
import { Button } from '@chakra-snippets/button';

import { FILTER_OPTIONS, getPreSelectedFilterType } from './helpers';

const EmptyNewInboxJob = ({
  totalChatCount,
}: {
  totalChatCount: string;
}): React.JSX.Element => {
  const [selectedFilter, setSelectedFilter] = useState<{
    refineBy: string | null;
    tags: string[];
  } | object>(FILTER_OPTIONS[0]);
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);

  const { inboxFilters, userName } = useSelector((state: StateType) => ({
    userName: `${state?.auth?.user?.firstName} ${state?.auth?.user?.lastName}`,
    inboxFilters: state?.profile?.properties?.inboxFilters || {},
  }));

  const preselectedFiltersType = useMemo(() => getPreSelectedFilterType(inboxFilters), [inboxFilters]);

  useEffect(() => {
    if (userName) {
      FILTER_OPTIONS[0].items.myChats = userName;
      FILTER_OPTIONS[1].items.myChats = userName;
      FILTER_OPTIONS[2].items.myChats = userName;
    }
  }, [userName]);

  const hasInboxFilterButtons = useCheckPermissions([
    FEATURE_FLAG_INBOX_FILTERS,
  ]);

  useEffect(() => {
    if (preselectedFiltersType && hasInboxFilterButtons) {
      setSelectedFilter(preselectedFiltersType);
    }
  }, [hasInboxFilterButtons, preselectedFiltersType]);

  const chatCount = Number(totalChatCount);
  let message = 'There are No Cases in the Inbox';
  if (chatCount === 1) {
    message = 'There is 1 Case in the Inbox';
  } else if (chatCount > 50) {
    message = 'There are 50+ Cases in the Inbox';
  } else if (chatCount > 1 && chatCount <= 50) {
    message = `There are ${chatCount} Cases in the Inbox`;
  }

  const handleRefineByFilter = (option: {
    refineBy: string | null;
    tags: string[];
  }) => {
    if (!hasInboxFilterButtons) {
      return;
    }
    setSelectedFilter(option);
    setIsSaving(true);
    const saveFilters = async () => {
      await dispatch(
        persistUserProperties({
          hasUsedNewInboxFilters: true,
          inboxFilters: {
            ...inboxFilters,
            ...option,
          },
        }),
      );
      setIsSaving(false);
    };

    saveFilters();
  };

  return (
    <div className="chatComponent chatComponent-state-box">
      <Text textStyle="4xl" textAlign="center">
        {message}
      </Text>
      <Flex
        flexDir="column"
        alignItems="center"
        columnGap={5}
        w="30vw"
        rowGap={5}
        marginTop={5}
      >
        {hasInboxFilterButtons ? (
          <>
            <Text textAlign="center">
              Please select a case from the inbox to view and start messaging or
              use a quick filter to find your most relevant messages
            </Text>
            <Flex gap="5" flexDir="column">
              <Button
                loading={selectedFilter === FILTER_OPTIONS[0] && isSaving}
                variant={
                  selectedFilter === FILTER_OPTIONS[0] ? 'solid' : 'outline'
                }
                onClick={() => handleRefineByFilter(FILTER_OPTIONS[0])}
                data-testid={
                  selectedFilter === FILTER_OPTIONS[0]
                    ? 'all-my-cases-selected'
                    : 'all-my-cases'
                }
              >
                All My Cases
              </Button>
              <Button
                loading={selectedFilter === FILTER_OPTIONS[1] && isSaving}
                variant={
                  selectedFilter === FILTER_OPTIONS[1] ? 'solid' : 'outline'
                }
                onClick={() => handleRefineByFilter(FILTER_OPTIONS[1])}
                data-testid={
                  selectedFilter === FILTER_OPTIONS[1]
                    ? 'unanswered-opt-ins-selected'
                    : 'unanswered-opt-ins'
                }
              >
                Unanswered Opt-Ins
              </Button>
              <Button
                loading={selectedFilter === FILTER_OPTIONS[2] && isSaving}
                variant={
                  selectedFilter === FILTER_OPTIONS[2] ? 'solid' : 'outline'
                }
                onClick={() => handleRefineByFilter(FILTER_OPTIONS[2])}
                data-testid={
                  selectedFilter === FILTER_OPTIONS[2]
                    ? 'response-recommended-selected'
                    : 'response-recommended'
                }
              >
                Response Recommended
              </Button>
            </Flex>
          </>
        ) : (
          <Text textAlign="center">
            {chatCount === 0
              ? 'Once new cases are created you will see them here'
              : 'Please select a case from the Inbox to view it and start messaging'}
          </Text>
        )}
      </Flex>
    </div>
  );
};

export default EmptyNewInboxJob;
